import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { useCheck } from 'Src/feature/check/useCheck';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import useRoute from 'Src/routes/useRoute';

interface FormModel {
  total: number;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

interface CustomCashModalProps {
  control: ReturnType<typeof useCheckPagePayment>['modal']['customCashAmount'];
}

const TEST_ID = {
  root: `CustomCashModal`,
  cta: {
    cancel: `CustomCashModal-cancel`,
    next: `CustomCashModal-next`,
  }
};

const CustomCashModalContent: FC<CustomCashModalProps> = ({ control }) => {
  const { t } = useTranslation();
  const { route } = useRoute();

  const paymentFlow = usePaymentFlow();
  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const checkGetOne = useCheck.query.getOne(checkId);
  const check = checkGetOne.data;

  const form = useForm<FormModel>({
    schema: object().shape({
      total: number().label(t('labels.amount')).required().moreThan(0),
    }),
  });

  const customCashAmount = form.state.watch('total');

  const subtitle = paymentFlow.basis === undefined
    ? t('payment.labels.amountPaidVsDue', { paid: check?.balance.paid ?? 0, due: check?.balance.unpaid ?? 0 })
    : t('payment.labels.cashBasis', { amount: paymentFlow.basis });

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('payment.labels.customCashAmount')} />
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center', gap: 'md' }}>
        <Form usedForm={form} omitChangeTracking>
          <Field type="currencyInput" name="total" subtitle={subtitle} />
        </Form>
      </ModalContent>
      <ModalActions>
        <ModalAction
          data-testid={TEST_ID.cta.cancel}
          label={t('labels.cancel')}
          color="secondary"
          onClick={control.close}
        />
        <ModalAction
          data-testid={TEST_ID.cta.next}
          label={t('labels.next')}
          color="primary"
          onClick={() => control.onNext(parseFloat(customCashAmount?.toString() ?? '0'))}
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const CustomCashModal: FC<CustomCashModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={control.close}
  >
    <CustomCashModalContent control={control} />
  </Modal>
);

export { CustomCashModal, TEST_ID };
export type { CustomCashModalProps };
